import { ethers } from 'ethers';
import { SUPPORTED_CHAIN_ID, SUPPORTED_NETWORK_NAME } from '../config/supportedChain';

/**
 * DID Identity Token ABI (ERC-721 Soulbound)
 */
/** Write methods on DID identity token (ethers BaseContract does not list custom ABI methods). */
type DIDIdentityTokenWritable = ethers.Contract & {
  createMyDid(): Promise<ethers.ContractTransactionResponse>;
  updateChatReference(
    chatReference: string,
    encryptedKeyMetadata: Uint8Array
  ): Promise<ethers.ContractTransactionResponse>;
  updateEncryptedKeyMetadata(
    encryptedKeyMetadata: Uint8Array
  ): Promise<ethers.ContractTransactionResponse>;
  updateChatDataReference(chatReference: string): Promise<ethers.ContractTransactionResponse>;
  anchorChatCommitment(commitment: ethers.BytesLike): Promise<ethers.ContractTransactionResponse>;
};

const DID_IDENTITY_TOKEN_ABI = [
  // Minting functions
  'function createMyDid() external returns (uint256 tokenId)',
  // Profile functions
  'function getProfileByOwner(address user) external view returns (tuple(address owner, uint64 createdAt, uint64 lastUpdatedAt, bytes32 chatDataReference, bytes encryptedKeyMetadata))',
  'function updateChatReference(string calldata newRef, bytes calldata newEncryptedKeyMetadata) external',
  'function updateEncryptedKeyMetadata(bytes calldata newEncryptedKeyMetadata) external',
  'function updateChatDataReference(string calldata newRef) external',
  // Hybrid anchoring
  'function anchorChatCommitment(bytes32 commitment) external',
  'function anchorCommitments(address, uint256) external view returns (bytes32)',
  // Basic DID functions
  'function hasDid(address user) external view returns (bool)',
  'function getDidId(address user) external view returns (uint256)',
  'function getChatDataReference(address user) external view returns (bytes32)',
  'function getEncryptedKeyMetadata(address user) external view returns (bytes memory)',
  // ERC-721 functions
  'function ownerOf(uint256 tokenId) external view returns (address)',
];

export type OnChainDidStatus = {
  hasDid: boolean;
  tokenId: string | null;
};

/**
 * Read DID ownership from the legacy identity token contract (client-side RPC).
 * Used when the backend registry check misses tokens minted on the legacy contract.
 */
export async function fetchOnChainDidStatus(
  provider: ethers.Provider,
  walletAddress: string,
): Promise<OnChainDidStatus> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    return { hasDid: false, tokenId: null };
  }

  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    provider,
  );

  const hasDid = await contract.hasDid(walletAddress);
  if (!hasDid) {
    return { hasDid: false, tokenId: null };
  }

  const tokenId = await contract.getDidId(walletAddress);
  return { hasDid: true, tokenId: tokenId.toString() };
}

/**
 * Get DID contract address from environment or use default
 */
function getDIDContractAddress(): string {
  // In production, this should come from environment variable
  // For now, return empty string (will be set when contract is deployed)
  return import.meta.env.VITE_DID_IDENTITY_TOKEN_ADDRESS || '';
}

const WRITE_TX_MAX_ATTEMPTS = 4;
const WRITE_TX_BASE_DELAY_MS = 900;

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableWriteError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /nonce|timeout|NETWORK_ERROR|rate limit|too many|ECONNRESET|ETIMEDOUT|replacement|underpriced|ECONNREFUSED|503|502/i.test(
    msg,
  );
}

/**
 * Send a contract write and wait for the receipt, with exponential backoff on
 * transient RPC / nonce errors (user still pays one successful tx gas).
 */
export async function sendContractWriteWithRetry(
  sendTx: () => Promise<ethers.ContractTransactionResponse>,
  options?: { maxAttempts?: number },
): Promise<ethers.ContractTransactionReceipt> {
  const maxAttempts = options?.maxAttempts ?? WRITE_TX_MAX_ATTEMPTS;
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const tx = await sendTx();
      const receipt = await tx.wait();
      if (receipt === null) {
        throw new Error('Transaction receipt not available');
      }
      return receipt;
    } catch (err) {
      lastError = err;
      if (attempt >= maxAttempts || !isRetryableWriteError(err)) {
        throw err;
      }
      await sleep(WRITE_TX_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
  }
  throw lastError;
}

/**
 * Translate a createMyDid write failure into a message the user can act on.
 * Contract reverts (e.g. public minting disabled) often reach the client as a
 * bare "missing revert data" CALL_EXCEPTION because the wallet bridge strips
 * the revert reason, so map them to something meaningful.
 */
function toDIDMintErrorMessage(err: unknown): string {
  const e: any = err;
  const raw = [e?.reason, e?.shortMessage, e?.message, e?.info?.error?.message, e?.data]
    .filter((v) => typeof v === 'string')
    .join(' ')
    .toLowerCase();

  if (raw.includes('public minting')) {
    return 'Safe ID Token minting is temporarily paused on this network. Please try again later.';
  }
  if (raw.includes('has did') || raw.includes('already exists')) {
    return 'You already have a Safe ID Token.';
  }
  if (raw.includes('insufficient') || raw.includes('needed for gas') || /not enough.*eth|funds/.test(raw)) {
    return 'You need ETH in your wallet to pay the network gas for this transaction. Add ETH and try again.';
  }
  if (e?.code === 'CALL_EXCEPTION' || /missing revert data|estimateGas|execution reverted/i.test(raw)) {
    return 'The Safe ID Token contract rejected the mint. Minting may be temporarily paused on this network. Please try again later.';
  }
  return e?.message || 'Failed to create Safe ID Token.';
}

/**
 * Create a DID for the caller (user self-mint)
 * The user's wallet signs and pays gas for this transaction.
 * No value is attached: the deployed identity token's createMyDid() is
 * non-payable and has no mint fee.
 *
 * @param signer The ethers signer (wallet owner)
 * @returns Transaction receipt and the new tokenId
 */
export async function createMyDid(
  signer: ethers.JsonRpcSigner
): Promise<{ receipt: ethers.ContractTransactionReceipt; tokenId: bigint }> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  // Verify network
  const network = await signer.provider.getNetwork();
  if (Number(network.chainId) !== SUPPORTED_CHAIN_ID) {
    throw new Error(`Please switch to ${SUPPORTED_NETWORK_NAME} to create your DID`);
  }

  // Get wallet address and verify no existing DID
  const walletAddress = await signer.getAddress();
  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    signer.provider,
  );

  const hasDid = await contract.hasDid(walletAddress);
  if (hasDid) {
    throw new Error('A DID already exists for this wallet.');
  }

  const contractWithSigner = contract.connect(signer) as DIDIdentityTokenWritable;

  let receipt: ethers.ContractTransactionReceipt;
  try {
    receipt = await sendContractWriteWithRetry(() =>
      contractWithSigner.createMyDid(),
    );
  } catch (err: any) {
    // Keep wallet cancellations and rejections intact so callers can rely on
    // their existing ACTION_REJECTED handling.
    if (err?.code === 'ACTION_REJECTED' || /user denied|transaction rejected|denied transaction|rejected transaction/i.test(String(err?.message || ''))) {
      throw err;
    }
    throw new Error(toDIDMintErrorMessage(err));
  }

  const tokenId = receipt.logs.find((log: ethers.Log) => {
    try {
      const parsed = contract.interface.parseLog(log);
      return parsed?.name === 'DidCreated';
    } catch {
      return false;
    }
  });

  let extractedTokenId: bigint;
  if (tokenId) {
    const parsed = contract.interface.parseLog(tokenId);
    extractedTokenId = (parsed?.args?.[1] as bigint) ?? 0n;
  } else {
    extractedTokenId = await contract.getDidId(walletAddress);
  }

  return { receipt, tokenId: extractedTokenId };
}

/**
 * Update DID's chat reference and encrypted key metadata on-chain
 * This must be called by the wallet owner (frontend)
 * 
 * @param signer The ethers signer (wallet owner)
 * @param chatReference The chat data reference (hash/CID/DB key as string)
 * @param encryptedKeyMetadata The encrypted key metadata (as bytes)
 * @returns Transaction receipt
 */
export async function updateChatReference(
  signer: ethers.JsonRpcSigner,
  chatReference: string,
  encryptedKeyMetadata: Uint8Array | string
): Promise<ethers.ContractTransactionReceipt> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  // Verify network
  const network = await signer.provider.getNetwork();
  if (Number(network.chainId) !== SUPPORTED_CHAIN_ID) {
    throw new Error(`Please switch to ${SUPPORTED_NETWORK_NAME} to update ID token data`);
  }

  // Get wallet address
  const walletAddress = await signer.getAddress();

  // Verify wallet has DID
  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    signer.provider
  );
  
  const hasDid = await contract.hasDid(walletAddress);
  if (!hasDid) {
    throw new Error('Wallet does not have an ID token. Please create one first.');
  }

  // Convert encrypted key metadata to bytes if needed
  let keyMetadataBytes: Uint8Array;
  if (typeof encryptedKeyMetadata === 'string') {
    // If it's a hex string, convert it
    if (encryptedKeyMetadata.startsWith('0x')) {
      keyMetadataBytes = ethers.getBytes(encryptedKeyMetadata);
    } else {
      // Otherwise, treat as base64 or plain string
      keyMetadataBytes = new TextEncoder().encode(encryptedKeyMetadata);
    }
  } else {
    keyMetadataBytes = encryptedKeyMetadata;
  }

  const contractWithSigner = contract.connect(signer) as DIDIdentityTokenWritable;

  return sendContractWriteWithRetry(() =>
    contractWithSigner.updateChatReference(chatReference, keyMetadataBytes),
  );
}

/**
 * Update only encrypted key metadata (preserve chat reference)
 * 
 * @param signer The ethers signer (wallet owner)
 * @param encryptedKeyMetadata The encrypted key metadata (as bytes)
 * @returns Transaction receipt
 */
export async function updateEncryptedKeyMetadata(
  signer: ethers.JsonRpcSigner,
  encryptedKeyMetadata: Uint8Array | string
): Promise<ethers.ContractTransactionReceipt> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  // Verify network
  const network = await signer.provider.getNetwork();
  if (Number(network.chainId) !== SUPPORTED_CHAIN_ID) {
    throw new Error(`Please switch to ${SUPPORTED_NETWORK_NAME} to update ID token data`);
  }

  // Get wallet address
  const walletAddress = await signer.getAddress();

  // Verify wallet has DID
  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    signer.provider
  );
  
  const hasDid = await contract.hasDid(walletAddress);
  if (!hasDid) {
    throw new Error('Wallet does not have an ID token. Please create one first.');
  }

  // Convert encrypted key metadata to bytes if needed
  let keyMetadataBytes: Uint8Array;
  if (typeof encryptedKeyMetadata === 'string') {
    if (encryptedKeyMetadata.startsWith('0x')) {
      keyMetadataBytes = ethers.getBytes(encryptedKeyMetadata);
    } else {
      keyMetadataBytes = new TextEncoder().encode(encryptedKeyMetadata);
    }
  } else {
    keyMetadataBytes = encryptedKeyMetadata;
  }

  const contractWithSigner = contract.connect(signer) as DIDIdentityTokenWritable;

  return sendContractWriteWithRetry(() =>
    contractWithSigner.updateEncryptedKeyMetadata(keyMetadataBytes),
  );
}

/**
 * Update only chat data reference (preserve encrypted key)
 * 
 * @param signer The ethers signer (wallet owner)
 * @param chatReference The chat data reference (hash/CID/DB key as string)
 * @returns Transaction receipt
 */
export async function updateChatDataReference(
  signer: ethers.JsonRpcSigner,
  chatReference: string
): Promise<ethers.ContractTransactionReceipt> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  // Verify network
  const network = await signer.provider.getNetwork();
  if (Number(network.chainId) !== SUPPORTED_CHAIN_ID) {
    throw new Error(`Please switch to ${SUPPORTED_NETWORK_NAME} to update ID token data`);
  }

  // Get wallet address
  const walletAddress = await signer.getAddress();

  // Verify wallet has DID
  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    signer.provider
  );
  
  const hasDid = await contract.hasDid(walletAddress);
  if (!hasDid) {
    throw new Error('Wallet does not have an ID token. Please create one first.');
  }

  const contractWithSigner = contract.connect(signer) as DIDIdentityTokenWritable;

  return sendContractWriteWithRetry(() => contractWithSigner.updateChatDataReference(chatReference));
}

/**
 * Get DID profile from contract
 * 
 * @param provider The ethers provider
 * @param walletAddress The wallet address
 * @returns DID profile with all fields
 */
export async function getDIDProfile(
  provider: ethers.Provider,
  walletAddress: string
): Promise<{
  owner: string;
  createdAt: bigint;
  lastUpdatedAt: bigint;
  chatDataReference: string;
  encryptedKeyMetadata: string;
}> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    provider
  );

  const profile = await contract.getProfileByOwner(walletAddress);
  return {
    owner: profile.owner,
    createdAt: profile.createdAt,
    lastUpdatedAt: profile.lastUpdatedAt,
    chatDataReference: profile.chatDataReference,
    encryptedKeyMetadata: profile.encryptedKeyMetadata,
  };
}

/**
 * Get chat data reference from contract
 * 
 * @param provider The ethers provider
 * @param walletAddress The wallet address
 * @returns Chat data reference (bytes32 as hex string)
 */
export async function getChatDataReference(
  provider: ethers.Provider,
  walletAddress: string
): Promise<string> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    provider
  );

  return await contract.getChatDataReference(walletAddress);
}

/**
 * Get encrypted key metadata from contract
 * 
 * @param provider The ethers provider
 * @param walletAddress The wallet address
 * @returns Encrypted key metadata (as hex string)
 */
export async function getEncryptedKeyMetadata(
  provider: ethers.Provider,
  walletAddress: string
): Promise<string> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    provider
  );

  const metadata = await contract.getEncryptedKeyMetadata(walletAddress);
  return metadata;
}

/**
 * Get the anchored chat commitment for a wallet's DID token
 * 
 * @param provider The ethers provider
 * @param walletAddress The wallet address
 * @returns The anchored commitment hash (bytes32 as hex string) or zero hash
 */
export async function getAnchorCommitment(
  provider: ethers.Provider,
  walletAddress: string
): Promise<string> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    provider
  );

  const tokenId = await contract.getDidId(walletAddress);
  return await contract.anchorCommitments(walletAddress, tokenId);
}

/**
 * Anchor a cryptographic commitment for chat data integrity on-chain.
 * This stores a keccak256 hash of the chat data — not the data itself.
 * Idempotent: the same commitment cannot be anchored twice.
 * 
 * @param signer The ethers signer (wallet owner)
 * @param commitment The keccak256 hash of the chat data (bytes32)
 * @returns Transaction receipt
 */
export async function anchorChatCommitment(
  signer: ethers.JsonRpcSigner,
  commitment: ethers.BytesLike
): Promise<ethers.ContractTransactionReceipt> {
  const contractAddress = getDIDContractAddress();
  if (!contractAddress) {
    throw new Error('DID contract address not configured');
  }

  // Verify network
  const network = await signer.provider.getNetwork();
  if (Number(network.chainId) !== SUPPORTED_CHAIN_ID) {
    throw new Error(`Please switch to ${SUPPORTED_NETWORK_NAME} to anchor chat commitment`);
  }

  // Get wallet address
  const walletAddress = await signer.getAddress();

  // Verify wallet has DID
  const contract = new ethers.Contract(
    contractAddress,
    DID_IDENTITY_TOKEN_ABI,
    signer.provider
  );
  
  const hasDid = await contract.hasDid(walletAddress);
  if (!hasDid) {
    throw new Error('Wallet does not have a DID. Please create one first.');
  }

  const contractWithSigner = contract.connect(signer) as DIDIdentityTokenWritable;

  return sendContractWriteWithRetry(() =>
    contractWithSigner.anchorChatCommitment(commitment),
  );
}

